import { useEffect, useState } from 'react';

/**
 * Theme preference hook — tri-state: 'light' | 'dark' | 'system'.
 *
 * - 'light'/'dark' pin the theme; 'system' follows the OS and live-updates when the OS flips.
 * - Persists the *preference* (not the resolved value) to localStorage 'theme'.
 * - Class-based toggling ('dark' on <html>).
 *
 * Exposes `{ isDark, toggle }` for the quick top-bar switch and `{ theme, setTheme }` for the
 * Settings control.
 */
export type ThemePref = 'light' | 'dark' | 'system';

const systemPrefersDark = () => window.matchMedia('(prefers-color-scheme: dark)').matches;

export function useDarkMode() {
  const [theme, setTheme] = useState<ThemePref>(() => {
    const stored = localStorage.getItem('theme');
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
  });
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const isDark = theme === 'dark' || (theme === 'system' && systemDark);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
    localStorage.setItem('theme', theme);
  }, [isDark, theme]);

  // Quick top-bar toggle: flip the *effective* mode to an explicit light/dark preference.
  const toggle = () =>
    setTheme((prev) => {
      const effective = prev === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : prev;
      return effective === 'dark' ? 'light' : 'dark';
    });

  return { isDark, theme, setTheme, toggle };
}
